import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { listRefunds, type RefundListItem } from '../../lib/order-refund';

interface RefundsSectionProps {
  werkOrderId: string;
  refreshKey?: number;
}

function statusBadgeClass(status: string): string {
  if (status === 'Verwerkt') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'Mislukt') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-amber-100 text-amber-800 border-amber-200';
}

function formatEuro(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `€ ${value.toFixed(2).replace('.', ',')}`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function RefundsSection({ werkOrderId, refreshKey }: RefundsSectionProps) {
  const [refunds, setRefunds] = useState<RefundListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listRefunds(werkOrderId)
      .then((rows) => {
        if (!cancelled) setRefunds(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Refund-lijst ophalen mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [werkOrderId, refreshKey]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Terugbetalingen</h3>
        <p className="text-xs text-slate-500">Laden…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Terugbetalingen</h3>
        <p className="text-xs text-red-600">{error}</p>
      </div>
    );
  }

  if (refunds.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Terugbetalingen (0)</h3>
        <p className="text-xs text-slate-400">Nog geen terugbetalingen</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">
        Terugbetalingen ({refunds.length})
      </h3>
      <ul className="divide-y divide-slate-100">
        {refunds.map((r) => (
          <li key={r.refundId} className="py-2 flex items-start justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-700">{r.refundName}</span>
                <span
                  className={clsx(
                    'px-1.5 py-0.5 text-[10px] font-medium border rounded',
                    statusBadgeClass(r.status)
                  )}
                >
                  {r.status}
                </span>
                {r.methode && (
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">
                    {r.methode}
                  </span>
                )}
                {r.refundReden && (
                  <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                    {r.refundReden}
                  </span>
                )}
                {r.bron && (
                  <span className="text-[10px] text-slate-400">via {r.bron}</span>
                )}
              </div>
              {r.reden && <p className="text-xs text-slate-600 mt-1">{r.reden}</p>}
              {r.foutmelding && (
                <p className="text-xs text-red-600 mt-1 break-words">{r.foutmelding}</p>
              )}
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="text-sm font-medium text-slate-900">
                {formatEuro(r.bedragInclBtw)}
              </div>
              <div className="text-[10px] text-slate-500">
                {formatDate(r.datumVerwerkt || r.createdDate)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
